// Copyright 2025, University of Maryland, All Rights Reserved
import { ReactNode, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/subsystems/workspaces';

type AuthState = ReturnType<typeof useAuth>;
export type AccessCheck = (auth: AuthState) => boolean;

interface RouteGuardProps {
  allow: AccessCheck;
  children: ReactNode;
}

export function RouteGuard({ allow, children }: RouteGuardProps) {
  const auth = useAuth();
  const location = useLocation();
  const allowed = allow(auth);

  useLayoutEffect(() => {
    if (!allowed) {
      window.location.replace(`${window.location.pathname}${window.location.search}#/${location.search}`);
    }
  }, [allowed, location.search]);

  if (!allowed) {
    return null;
  }
  return <>{children}</>;
}

export const isGlobalAdmin: AccessCheck = (a) => !!a.isGlobalAdmin;
export const isGlobalOrWorkspaceAdmin: AccessCheck = (a) => !!a.isGlobalAdmin || !!a.isWorkspaceAdmin;
// Organization admins manage their tenant and inherit workspace administration,
// while the backend still scopes every operation to the selected organization.
export const canManageUsers: AccessCheck = (a) =>
  !!a.isGlobalAdmin || !!a.isOrgAdmin || !!a.isWorkspaceAdmin;
